import { useState } from 'react'
import { api, ApiError, type Response } from '@/lib/api'
import { usePageTitle } from '@/hooks/usePageTitle'
import './changePassword.css'

/**
 * Port of Views/Profile/ChangePassword.cshtml +
 * wwwroot/js/app/profile/changePassword.js.
 *
 * Validation rules and their exact wording are preserved, as are the inline
 * success/error banners — this page never used SweetAlert.
 *
 * The `Email` field the form posted is gone: the endpoint takes the address from
 * the session (as the C# controller already did, overriding whatever was sent),
 * so there is nothing for the client to supply.
 *
 * The original's 800ms `setTimeout` around the success message and the button
 * reset is dropped. It served no purpose beyond making a completed save look
 * slower than it was.
 */
interface FieldErrors {
  oldPassword?: string
  newPassword?: string
  confirmNewPassword?: string
}

export function ChangePassword(): JSX.Element {
  usePageTitle('Change Password')

  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [visible, setVisible] = useState<Record<string, boolean>>({})

  /** Port of `Profile.ValidateChangePasswordForm`, messages verbatim. */
  function validate(): FieldErrors {
    const next: FieldErrors = {}

    if (!oldPassword.trim()) next.oldPassword = 'Old password is required'

    if (!newPassword.trim()) next.newPassword = 'New password is required'
    else if (newPassword.length < 6) {
      next.newPassword = 'New password must be at least 6 characters long'
    }

    if (!confirmNewPassword.trim()) {
      next.confirmNewPassword = 'Confirm password is required'
    } else if (newPassword !== confirmNewPassword) {
      next.confirmNewPassword = 'New password and confirm password do not match'
    }

    // Checked last, and it overwrites any newPassword message set above —
    // matching the original's ordering.
    if (oldPassword && newPassword && oldPassword === newPassword) {
      next.newPassword = 'New password must be different from old password'
    }

    return next
  }

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault()

    setSuccessMessage('')
    setErrorMessage('')

    const problems = validate()
    setErrors(problems)
    if (Object.keys(problems).length > 0) return

    setIsSaving(true)
    try {
      const response = await api.post<Response<string>>('/api/ecareer/profile/change-password', {
        OldPassword: oldPassword,
        NewPassword: newPassword,
        ConfirmPassword: confirmNewPassword,
      })

      if (!response.Success) {
        setErrorMessage(response.Message ?? 'Failed to change password')
        return
      }

      setSuccessMessage('Password changed successfully!')
      setOldPassword('')
      setNewPassword('')
      setConfirmNewPassword('')
      setErrors({})
    } catch (error) {
      // A 401 is already handled globally in lib/api (it redirects to the login
      // page), so only real failures reach here.
      setErrorMessage(
        error instanceof ApiError
          ? error.message
          : 'An error occurred while changing password',
      )
    } finally {
      setIsSaving(false)
    }
  }

  function renderField(
    id: 'oldPassword' | 'newPassword' | 'confirmNewPassword',
    label: string,
    value: string,
    onChange: (value: string) => void,
    marginClass: string,
  ): JSX.Element {
    const isVisible = visible[id] ?? false
    return (
      <div className={marginClass}>
        <label htmlFor={id} className="form-label">
          {label} <span className="required">*</span>
        </label>
        <div className="input-password-wrapper">
          <input
            type={isVisible ? 'text' : 'password'}
            className="form-control"
            id={id}
            name={id}
            required
            value={value}
            onChange={(event) => onChange(event.target.value)}
          />
          <button
            type="button"
            className="toggle-password"
            data-target={id}
            tabIndex={-1}
            onClick={() => setVisible((previous) => ({ ...previous, [id]: !isVisible }))}
          >
            <i className={`fas ${isVisible ? 'fa-eye-slash' : 'fa-eye'}`} />
          </button>
        </div>
        {errors[id] && <div className="error-message text-danger small mt-1">{errors[id]}</div>}
      </div>
    )
  }

  return (
    <div className="content">
      <section className="bg-white">
        <div className="container">
          <div className="row g-4">
            <div className="accordion card p-3 mb-0" id="pnlChangePassword">
              <div className="accordion-item">
                <h2 className="accordion-header" id="changePasswordSection">
                  <button
                    className="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseChangePassword"
                    aria-expanded="true"
                    aria-controls="collapseChangePassword"
                  >
                    <span
                      className="badge text-bg-primary p-2 m-r-10 bold"
                      style={{ fontSize: '1.2em' }}
                    >
                      <i className="fas fa-lock" />
                    </span>
                    <h5 className="mb-0"> Confidential Information </h5>
                  </button>
                </h2>

                <div
                  id="collapseChangePassword"
                  className="accordion-collapse collapse show"
                  aria-labelledby="changePasswordSection"
                >
                  <div className="accordion-body">
                    <div className="card-body">
                      {successMessage && (
                        <div className="alert alert-success" role="alert" id="successMessage">
                          <b>Success!</b> {successMessage}
                        </div>
                      )}

                      {errorMessage && (
                        <div className="alert alert-danger" role="alert" id="errorMessage">
                          <b>Error!</b> <span id="errorText">{errorMessage}</span>
                        </div>
                      )}

                      <form id="changePasswordForm" onSubmit={(event) => void handleSubmit(event)}>
                        {renderField(
                          'oldPassword',
                          'Old Password',
                          oldPassword,
                          setOldPassword,
                          'mb-3',
                        )}
                        {renderField(
                          'newPassword',
                          'New Password',
                          newPassword,
                          setNewPassword,
                          'mb-3',
                        )}
                        {renderField(
                          'confirmNewPassword',
                          'Confirm New Password',
                          confirmNewPassword,
                          setConfirmNewPassword,
                          'mb-4',
                        )}

                        <div className="text-center">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            id="btnSave"
                            disabled={isSaving}
                          >
                            {isSaving ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
